import { useMemo, useState } from 'react';
import {
  ETIQUETAS_MOTIVO_PERDIDA,
  ETIQUETAS_PERFIL_COMPETIDOR,
  ETIQUETAS_TIPO_ORGANISMO,
  MOTIVOS_PERDIDA,
  PERFILES_COMPETIDOR,
  TIPOS_ORGANISMO,
  esRutValido,
  formatearCLP,
  formatearDias,
  formatearPct,
  formatearRut,
  type ActualizarCompetidorInput,
  type CrearCompetidorInput,
  type FiltrosCompetencia,
  type FusionarCompetidoresInput,
  type MotivoPerdida,
  type PerfilCompetidor,
  type RegistrarActaLicitacionInput,
  type SimularOfertaCompetidorInput,
  type TipoOrganismo,
} from '@lexicore/shared';
import {
  IconoAlerta,
  IconoBuscar,
  IconoCerrar,
  IconoCheck,
  IconoEditar,
  IconoMas,
  IconoRayo,
  IconoRestablecer,
} from '../componentes/Iconos';
import {
  Cargando,
  ErrorCarga,
  Panel,
  TarjetaKpi,
  Vacio,
} from '../componentes/UI';
import { useAuth } from '../contextos/AuthContext';
import { useAvisos } from '../contextos/AvisosContext';
import {
  useCategoriasComercialesConfig,
  useHeadToHead,
  useLicitaciones,
  useSimularPrecio,
  useTableroCompetencia,
  type CompetidorDirectorio,
} from '../lib/consultas';
import {
  useActualizarCompetidor,
  useCrearCompetidor,
  useFusionarCompetidores,
  useRegistrarActaCompetencia,
} from '../lib/mutaciones';

type PestañaCompetencia = 'radar' | 'banco' | 'simulador' | 'sectores';

function obtenerBadgePerfil(perfil: PerfilCompetidor) {
  switch (perfil) {
    case 'DEPREDADOR_PRECIO':
      return {
        etiqueta: '🏷️ Depredador de Precios',
        descripcion: 'Adjudica a <85% del presupuesto oficial',
        fondo: 'rgba(239, 68, 68, 0.15)',
        color: 'var(--accent-red)',
        borde: '1px solid rgba(239, 68, 68, 0.35)',
      };
    case 'AGRESIVO_PLAZO':
      return {
        etiqueta: '⚡ Agresivo en Plazos',
        descripcion: 'Promete entregas significativamente más rápidas',
        fondo: 'rgba(245, 158, 11, 0.15)',
        color: 'var(--accent-yellow)',
        borde: '1px solid rgba(245, 158, 11, 0.35)',
      };
    case 'LIDER_TECNICO':
      return {
        etiqueta: '🏆 Líder Técnico',
        descripcion: 'Precio alto, gana por experiencia y pauta técnica',
        fondo: 'rgba(139, 92, 246, 0.15)',
        color: 'var(--accent-purple)',
        borde: '1px solid rgba(139, 92, 246, 0.35)',
      };
    case 'EQUILIBRADO':
    default:
      return {
        etiqueta: '⚖️ Equilibrado / Estándar',
        descripcion: 'Comportamiento estándar de mercado',
        fondo: 'rgba(59, 130, 246, 0.15)',
        color: 'var(--accent-blue)',
        borde: '1px solid rgba(59, 130, 246, 0.35)',
      };
  }
}

export function Competencia() {
  const { usuario } = useAuth();
  const esGestorOAdmin = usuario?.rol === 'ADMIN' || usuario?.rol === 'OWNER' || usuario?.rol === 'GESTOR';
  const esAdmin = usuario?.rol === 'ADMIN' || usuario?.rol === 'OWNER';

  // Pestañas
  const [pestañaActiva, setPestañaActiva] = useState<PestañaCompetencia>('radar');

  // Filtros reactivos
  const [busqueda, setBusqueda] = useState('');
  const [sectorFiltro, setSectorFiltro] = useState<TipoOrganismo | ''>('');
  const [categoriaIdFiltro, setCategoriaIdFiltro] = useState('');
  const [ordenarPor, setOrdenarPor] = useState<'victorias' | 'brechaPrecio' | 'montoGanado' | 'alfabetico'>('victorias');

  // Modales
  const [modalActaAbierto, setModalActaAbierto] = useState(false);
  const [competidorH2HId, setCompetidorH2HId] = useState<string | null>(null);
  const [modalFusionAbierto, setModalFusionAbierto] = useState(false);
  const [modalCrearAbierto, setModalCrearAbierto] = useState(false);
  const [competidorParaEditar, setCompetidorParaEditar] = useState<CompetidorDirectorio | null>(null);

  // Parámetros para pre-cargar simulador
  const [simuladorPreseleccion, setSimuladorPreseleccion] = useState<{
    competidorId?: string;
    perfil?: PerfilCompetidor;
  } | null>(null);

  // Consulta de Categorías Comerciales (para filtro)
  const categoriasQuery = useCategoriasComercialesConfig();

  // Consulta Tablero Analítico Principal
  const filtrosQuery: FiltrosCompetencia = useMemo(() => ({
    busqueda: busqueda.trim() || undefined,
    sector: sectorFiltro || undefined,
    categoriaId: categoriaIdFiltro || undefined,
    ordenarPor,
  }), [busqueda, sectorFiltro, categoriaIdFiltro, ordenarPor]);

  const { data, isLoading, isError, error, refetch } = useTableroCompetencia(filtrosQuery);

  const hayFiltrosActivos = busqueda.trim() !== '' || sectorFiltro !== '' || categoriaIdFiltro !== '' || ordenarPor !== 'victorias';

  const resetearFiltros = () => {
    setBusqueda('');
    setSectorFiltro('');
    setCategoriaIdFiltro('');
    setOrdenarPor('victorias');
  };

  const abrirSimuladorConCompetidor = (competidor: CompetidorDirectorio) => {
    setSimuladorPreseleccion({
      competidorId: competidor.id,
      perfil: competidor.perfilEstrategico,
    });
    setPestañaActiva('simulador');
  };

  if (isLoading) return <Cargando texto="Cargando radar de competencia e inteligencia de mercado…" />;
  if (isError) return <ErrorCarga error={error} reintentar={() => void refetch()} />;
  if (!data) return null;

  const { kpis, metricasPorSector, bancoPreciosGanadores, directorio } = data;

  return (
    <>
      {/* ── Cabecera Ejecutiva ────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.01em' }}>
            Competencia & Inteligencia de Mercado
          </h1>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Análisis cara a cara (Head-to-Head), brecha de precios y perfiles de rivales en Mercado Público.
            Transforma cada derrota en la fórmula matemática para ganar la próxima licitación.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {esGestorOAdmin && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setModalActaAbierto(true)}
              title="Registrar oferta ganadora de un acta de adjudicación pública"
            >
              <IconoRayo size={15} />
              ⚡ Registrar Acta (30 seg)
            </button>
          )}

          {esAdmin && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalFusionAbierto(true)}
              title="Fusionar competidores duplicados conservando historial y notas"
            >
              🔀 Fusionar Rivales
            </button>
          )}

          {esGestorOAdmin && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalCrearAbierto(true)}
              title="Agregar un nuevo competidor al directorio"
            >
              <IconoMas size={14} />
              Nuevo Rival
            </button>
          )}
        </div>
      </div>

      {/* ── 4 KPIs Superiores (Primer Pliegue Sin Scroll) ─────────── */}
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <TarjetaKpi
          etiqueta="Win Rate Cara a Cara"
          valor={`${kpis.winRateCaraACara}%`}
          acento={kpis.winRateCaraACara >= 50 ? 'var(--accent-green)' : 'var(--accent-red)'}
          colorValor={kpis.winRateCaraACara >= 50 ? 'var(--accent-green)' : 'var(--accent-red)'}
          nota={
            kpis.totalEnfrentamientos > 0
              ? `${kpis.totalGanadas} Ganadas / ${kpis.totalPerdidas} Perdidas (${kpis.totalEnfrentamientos} directos)`
              : 'Sin enfrentamientos con competidores'
          }
        />

        <TarjetaKpi
          etiqueta="Brecha de Precio Promedio"
          valor={
            kpis.brechaPrecioPromedioPct === null
              ? '—'
              : `${kpis.brechaPrecioPromedioPct > 0 ? '+' : ''}${formatearPct(kpis.brechaPrecioPromedioPct, 1)}`
          }
          acento={
            kpis.brechaPrecioPromedioPct !== null && kpis.brechaPrecioPromedioPct > 0
              ? 'var(--accent-red)'
              : 'var(--accent-green)'
          }
          colorValor={
            kpis.brechaPrecioPromedioPct !== null && kpis.brechaPrecioPromedioPct > 0
              ? 'var(--accent-red)'
              : 'var(--accent-green)'
          }
          nota={
            kpis.brechaPrecioPromedioPct === null
              ? 'Sin montos de rivales ganadores'
              : kpis.brechaPrecioPromedioPct > 0
                ? 'Ofertamos más caro que el ganador'
                : 'Ofertamos más barato que el ganador'
          }
        />

        <TarjetaKpi
          etiqueta="Dinero Perdido en Disputa"
          valor={formatearCLP(kpis.dineroPerdido)}
          acento="var(--accent-gray)"
          nota="Monto total adjudicado a rivales donde postulamos"
        />

        <TarjetaKpi
          etiqueta="Causa Principal de Derrota"
          valor={
            kpis.causaPrincipalDerrota !== 'NINGUNA'
              ? (ETIQUETAS_MOTIVO_PERDIDA[kpis.causaPrincipalDerrota as MotivoPerdida] ?? kpis.causaPrincipalDerrota)
              : 'Sin derrotas'
          }
          acento="var(--accent-yellow)"
          colorValor="var(--accent-yellow)"
          nota={
            kpis.distribucionCausas.length > 0
              ? `${kpis.distribucionCausas[0]?.porcentaje ?? 0}% de las derrotas por este motivo`
              : 'Registro impecable'
          }
        />
      </div>

      {/* ── Navegación por Pestañas ───────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          borderBottom: '1px solid var(--border-subtle)',
          marginBottom: 20,
          overflowX: 'auto',
          paddingBottom: 2,
        }}
      >
        <button
          type="button"
          onClick={() => setPestañaActiva('radar')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: pestañaActiva === 'radar' ? '2px solid var(--accent-red)' : '2px solid transparent',
            color: pestañaActiva === 'radar' ? 'var(--text-primary)' : 'var(--text-muted)',
            fontWeight: pestañaActiva === 'radar' ? 700 : 500,
            fontSize: 13.5,
            padding: '10px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            whiteSpace: 'nowrap',
          }}
        >
          <span>⚔️</span> Radar de Competidores ({directorio.length})
        </button>

        <button
          type="button"
          onClick={() => setPestañaActiva('banco')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: pestañaActiva === 'banco' ? '2px solid var(--accent-red)' : '2px solid transparent',
            color: pestañaActiva === 'banco' ? 'var(--text-primary)' : 'var(--text-muted)',
            fontWeight: pestañaActiva === 'banco' ? 700 : 500,
            fontSize: 13.5,
            padding: '10px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            whiteSpace: 'nowrap',
          }}
        >
          <span>📊</span> Banco de Precios Ganadores
        </button>

        <button
          type="button"
          onClick={() => setPestañaActiva('simulador')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: pestañaActiva === 'simulador' ? '2px solid var(--accent-red)' : '2px solid transparent',
            color: pestañaActiva === 'simulador' ? 'var(--text-primary)' : 'var(--text-muted)',
            fontWeight: pestañaActiva === 'simulador' ? 700 : 500,
            fontSize: 13.5,
            padding: '10px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            whiteSpace: 'nowrap',
          }}
        >
          <span>🎯</span> Simulador de Precio Óptimo
        </button>

        <button
          type="button"
          onClick={() => setPestañaActiva('sectores')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: pestañaActiva === 'sectores' ? '2px solid var(--accent-red)' : '2px solid transparent',
            color: pestañaActiva === 'sectores' ? 'var(--text-primary)' : 'var(--text-muted)',
            fontWeight: pestañaActiva === 'sectores' ? 700 : 500,
            fontSize: 13.5,
            padding: '10px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            whiteSpace: 'nowrap',
          }}
        >
          <span>🏛️</span> Métricas por Sector ({metricasPorSector.length})
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          PESTAÑA 1: RADAR DE COMPETIDORES
      ────────────────────────────────────────────────────────────── */}
      {pestañaActiva === 'radar' && (
        <>
          {/* Barra de Filtros */}
          <div
            className="panel"
            style={{
              padding: '14px 18px',
              marginBottom: 20,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {/* Buscador */}
            <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
              <IconoBuscar
                size={16}
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }}
              />
              <input
                type="search"
                placeholder="Buscar rival por nombre o RUT…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: 36,
                  paddingRight: 12,
                  height: 38,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                }}
              />
            </div>

            {/* Selector de Sector */}
            <div style={{ flex: '0 1 200px', minWidth: 160 }}>
              <select
                value={sectorFiltro}
                onChange={(e) => setSectorFiltro(e.target.value as TipoOrganismo | '')}
                style={{
                  width: '100%',
                  height: 38,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  padding: '0 10px',
                }}
              >
                <option value="">Todos los sectores</option>
                {TIPOS_ORGANISMO.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {ETIQUETAS_TIPO_ORGANISMO[tipo]}
                  </option>
                ))}
              </select>
            </div>

            {/* Selector de Categoría Comercial */}
            <div style={{ flex: '0 1 220px', minWidth: 180 }}>
              <select
                value={categoriaIdFiltro}
                onChange={(e) => setCategoriaIdFiltro(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  padding: '0 10px',
                }}
              >
                <option value="">Todas las líneas de negocio</option>
                {categoriasQuery.data?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Selector de Orden */}
            <div style={{ flex: '0 1 190px', minWidth: 160 }}>
              <select
                value={ordenarPor}
                onChange={(e) =>
                  setOrdenarPor(
                    e.target.value as 'victorias' | 'brechaPrecio' | 'montoGanado' | 'alfabetico',
                  )
                }
                style={{
                  width: '100%',
                  height: 38,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  padding: '0 10px',
                }}
              >
                <option value="victorias">Más victorias</option>
                <option value="brechaPrecio">Menor brecha de precio</option>
                <option value="montoGanado">Mayor monto ganado</option>
                <option value="alfabetico">Alfabético (A-Z)</option>
              </select>
            </div>

            {/* Botón Restablecer */}
            {hayFiltrosActivos && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={resetearFiltros}
                title="Limpiar todos los filtros"
                style={{ height: 38 }}
              >
                <IconoRestablecer size={14} />
                Limpiar
              </button>
            )}
          </div>

          {/* Rejilla de Tarjetas de Competidores */}
          {directorio.length === 0 ? (
            <Panel>
              <Vacio
                titulo="No se encontraron competidores"
                detalle={
                  hayFiltrosActivos
                    ? 'No hay rivales que coincidan con los filtros aplicados. Prueba quitando o ampliando la búsqueda.'
                    : 'Aún no hay competidores registrados. Puedes registrar ofertas de las actas de licitación o agregar rivales manualmente.'
                }
                accion={
                  hayFiltrosActivos ? (
                    <button type="button" className="btn btn-secondary" onClick={resetearFiltros}>
                      Restablecer filtros
                    </button>
                  ) : esGestorOAdmin ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setModalActaAbierto(true)}
                    >
                      <IconoRayo size={15} />
                      ⚡ Registrar Primera Acta
                    </button>
                  ) : undefined
                }
              />
            </Panel>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: 16,
              }}
            >
              {directorio.map((rival) => {
                const badge = obtenerBadgePerfil(rival.perfilEstrategico);

                return (
                  <div
                    key={rival.id}
                    className="panel"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      padding: 18,
                    }}
                  >
                    <div>
                      {/* Cabecera Tarjeta: Nombre e Insignia de Perfil */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 10,
                          marginBottom: 8,
                        }}
                      >
                        <div>
                          <h3
                            style={{
                              fontSize: 16,
                              fontWeight: 700,
                              color: 'var(--text-primary)',
                              marginBottom: 2,
                            }}
                          >
                            {rival.nombre}
                          </h3>
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                            {rival.rut && esRutValido(rival.rut)
                              ? `RUT: ${formatearRut(rival.rut)}`
                              : rival.rut
                                ? `RUT: ${rival.rut}`
                                : 'Sin RUT registrado'}
                            {rival.sitioWeb && (
                              <>
                                {' · '}
                                <a
                                  href={
                                    rival.sitioWeb.startsWith('http')
                                      ? rival.sitioWeb
                                      : `https://${rival.sitioWeb}`
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}
                                >
                                  Web ↗
                                </a>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Badge de Perfil Estratégico */}
                        <span
                          title={badge.descripcion}
                          style={{
                            background: badge.fondo,
                            color: badge.color,
                            border: badge.borde,
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: 'var(--radius-full)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {badge.etiqueta}
                        </span>
                      </div>

                      {/* Cara a Cara y Estadísticas Clave */}
                      <div
                        style={{
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '12px 14px',
                          marginTop: 12,
                          marginBottom: 12,
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 10,
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                            Cara a Cara Directo
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color:
                                rival.caraACara.winRateVsRivalPct >= 50
                                  ? 'var(--accent-green)'
                                  : 'var(--accent-red)',
                              marginTop: 2,
                            }}
                          >
                            {rival.caraACara.ganadasPorNosotros}G vs {rival.caraACara.ganadasPorRival}P
                            <span style={{ fontSize: 11, fontWeight: 500, marginLeft: 4 }}>
                              ({rival.caraACara.winRateVsRivalPct}% éxito)
                            </span>
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                            Brecha Media de Precio
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color:
                                rival.brechaPrecioPromedioPct !== null &&
                                rival.brechaPrecioPromedioPct > 0
                                  ? 'var(--accent-red)'
                                  : 'var(--accent-green)',
                              marginTop: 2,
                            }}
                          >
                            {rival.brechaPrecioPromedioPct !== null
                              ? `${rival.brechaPrecioPromedioPct > 0 ? '+' : ''}${formatearPct(rival.brechaPrecioPromedioPct, 1)}`
                              : '—'}
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                            Ratio Presupuesto Rival
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                              marginTop: 2,
                            }}
                          >
                            {rival.ratioPresupuestoPromedioPct !== null
                              ? `~${rival.ratioPresupuestoPromedioPct}% del oficial`
                              : 'Sin adjudicaciones'}
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                            Monto que nos ganó
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                              marginTop: 2,
                            }}
                          >
                            {formatearCLP(rival.montoGanado)}
                          </div>
                        </div>
                      </div>

                      {/* Terreno Fuerte */}
                      {rival.terrenoFuerte && rival.terrenoFuerte.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--text-muted)',
                              marginBottom: 4,
                              fontWeight: 600,
                            }}
                          >
                            🏛️ Su Terreno Fuerte:
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {rival.terrenoFuerte.map((t) => (
                              <span
                                key={t.id}
                                style={{
                                  fontSize: 11,
                                  background: 'var(--bg-surface)',
                                  border: '1px solid var(--border-subtle)',
                                  padding: '2px 7px',
                                  borderRadius: 'var(--radius-sm)',
                                  color: 'var(--text-secondary)',
                                }}
                              >
                                {t.nombre} ({t.victorias})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Notas de Inteligencia */}
                      {rival.notas && (
                        <div
                          style={{
                            fontSize: 11.5,
                            fontStyle: 'italic',
                            color: 'var(--text-secondary)',
                            background: 'rgba(255, 255, 255, 0.03)',
                            borderLeft: '2px solid var(--accent-purple)',
                            padding: '6px 10px',
                            borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
                            marginBottom: 12,
                          }}
                        >
                          "{rival.notas}"
                        </div>
                      )}
                    </div>

                    {/* Botones de Acción Rápida */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        marginTop: 10,
                        borderTop: '1px solid var(--border-subtle)',
                        paddingTop: 12,
                        flexWrap: 'wrap',
                      }}
                    >
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ flex: 1 }}
                        onClick={() => setCompetidorH2HId(rival.id)}
                        title="Ver historial cara a cara detallado"
                      >
                        ⚔️ Cara a Cara
                      </button>

                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        style={{ flex: 1 }}
                        onClick={() => abrirSimuladorConCompetidor(rival)}
                        title="Simular oferta óptima contra este competidor"
                      >
                        🎯 Simular Precio
                      </button>

                      {esGestorOAdmin && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setCompetidorParaEditar(rival)}
                          title="Editar notas o datos del competidor"
                        >
                          <IconoEditar size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ─────────────────────────────────────────────────────────────
          PESTAÑA 2: BANCO DE PRECIOS GANADORES
      ────────────────────────────────────────────────────────────── */}
      {pestañaActiva === 'banco' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div
            className="panel"
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(139, 92, 246, 0.05))',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              Banco de Precios Ganadores ("Para Poder Mejorarlo")
            </h3>
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 840 }}>
              En Mercado Público cada licitación que se resuelve contiene el dato estadístico más valioso del mercado:
              <strong> a qué porcentaje del Presupuesto Oficial adjudican los ganadores</strong>.
              Este banco procesa de manera continua todas las actas públicas para que el equipo comercial cotice con
              precisión milimétrica y nunca vuelva a perder por cobrar a ciegas.
            </p>
          </div>

          <div className="kpi-grid">
            <TarjetaKpi
              etiqueta="Ratio Promedio de Adjudicación"
              valor={
                bancoPreciosGanadores.promedioRatioAdjudicacionPct !== null
                  ? `${bancoPreciosGanadores.promedioRatioAdjudicacionPct}%`
                  : '—'
              }
              acento="var(--accent-blue)"
              colorValor="var(--accent-blue)"
              nota="Porcentaje del presupuesto oficial neto al que se adjudica"
            />

            <TarjetaKpi
              etiqueta="Rango Histórico de Mercado"
              valor={
                bancoPreciosGanadores.rangoMinimoPct !== null
                  ? `${bancoPreciosGanadores.rangoMinimoPct}% – ${bancoPreciosGanadores.rangoMaximoPct}%`
                  : '—'
              }
              acento="var(--accent-purple)"
              nota="Dispersión entre la oferta más baja y más alta registrada"
            />

            <TarjetaKpi
              etiqueta="Muestra de Licitaciones"
              valor={bancoPreciosGanadores.totalMuestra}
              acento="var(--accent-green)"
              nota="Procesos adjudicados analizados en el sistema"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 16 }}>
            {/* Por Sector del Estado */}
            <Panel titulo="Ratios de Adjudicación por Sector del Estado" subtitulo="Comportamiento del precio según el tipo de comprador público">
              {bancoPreciosGanadores.porSector.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                  Aún no hay suficientes actas registradas para calcular ratios por sector.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {bancoPreciosGanadores.porSector.map((s) => (
                    <div key={s.sector}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600 }}>
                          {ETIQUETAS_TIPO_ORGANISMO[s.sector as TipoOrganismo] ?? s.sector}
                        </span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent-blue)' }}>
                          {s.ratioPromedioPct}%{' '}
                          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>
                            ({s.muestraTotal} actas)
                          </span>
                        </span>
                      </div>
                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${Math.min(100, s.ratioPromedioPct)}%`,
                            background: 'linear-gradient(90deg, var(--accent-blue), var(--accent-purple))',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {/* Por Categoría Comercial */}
            <Panel titulo="Ratios de Adjudicación por Línea de Negocio" subtitulo="Margen con el que compite el mercado según el tipo de servicio">
              {bancoPreciosGanadores.porCategoria.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                  Aún no hay suficientes actas registradas para calcular ratios por categoría.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {bancoPreciosGanadores.porCategoria.map((c) => (
                    <div key={c.categoriaId}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600 }}>{c.categoriaNombre}</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent-green)' }}>
                          {c.ratioPromedioPct}%{' '}
                          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>
                            ({c.muestraTotal} actas)
                          </span>
                        </span>
                      </div>
                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${Math.min(100, c.ratioPromedioPct)}%`,
                            background: 'linear-gradient(90deg, var(--accent-green), var(--accent-blue))',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          PESTAÑA 3: SIMULADOR DE PRECIO ÓPTIMO
      ────────────────────────────────────────────────────────────── */}
      {pestañaActiva === 'simulador' && (
        <SeccionSimulador
          competidores={directorio}
          preseleccion={simuladorPreseleccion}
        />
      )}

      {/* ─────────────────────────────────────────────────────────────
          PESTAÑA 4: DESGLOSE POR SECTOR DEL ESTADO
      ────────────────────────────────────────────────────────────── */}
      {pestañaActiva === 'sectores' && (
        <Panel
          titulo="Rendimiento y Penetración por Sector del Estado"
          subtitulo="Comparativa de volumen disputado, efectividad y rival dominante en cada nicho institucional"
        >
          {metricasPorSector.length === 0 ? (
            <Vacio
              titulo="Sin procesos concluidos por sector"
              detalle="A medida que registres licitaciones adjudicadas o perdidas, se desglosará el comportamiento por cada sector público."
            />
          ) : (
            <div className="tabla-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Sector Institucional</th>
                    <th style={{ textAlign: 'center' }}>Total Procesos</th>
                    <th style={{ textAlign: 'center' }}>Ganadas vs Perdidas</th>
                    <th style={{ textAlign: 'center' }}>Win Rate %</th>
                    <th style={{ textAlign: 'right' }}>Brecha Precio Media</th>
                    <th style={{ textAlign: 'right' }}>Mercado en Juego</th>
                    <th>Rival Dominante</th>
                  </tr>
                </thead>
                <tbody>
                  {metricasPorSector.map((s) => (
                    <tr key={s.sector}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {ETIQUETAS_TIPO_ORGANISMO[s.sector] ?? s.sector}
                      </td>
                      <td style={{ textAlign: 'center' }}>{s.totalProcesos}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ color: 'var(--accent-green)', fontWeight: 600 }}>{s.ganadas}G</span>
                        {' / '}
                        <span style={{ color: 'var(--accent-red)', fontWeight: 600 }}>{s.perdidas}P</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            color: s.winRatePct >= 50 ? 'var(--accent-green)' : 'var(--accent-red)',
                          }}
                        >
                          {s.winRatePct}%
                        </span>
                      </td>
                      <td
                        className="td-mono"
                        style={{
                          textAlign: 'right',
                          color:
                            s.brechaPrecioPromedioPct !== null && s.brechaPrecioPromedioPct > 0
                              ? 'var(--accent-red)'
                              : 'var(--accent-green)',
                        }}
                      >
                        {s.brechaPrecioPromedioPct !== null
                          ? `${s.brechaPrecioPromedioPct > 0 ? '+' : ''}${formatearPct(s.brechaPrecioPromedioPct, 1)}`
                          : '—'}
                      </td>
                      <td className="td-mono" style={{ textAlign: 'right' }}>
                        {formatearCLP(s.montoMercadoNeto)}
                      </td>
                      <td>
                        {s.rivalDominante ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                              {s.rivalDominante.nombre}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                background: 'rgba(239, 68, 68, 0.15)',
                                color: 'var(--accent-red)',
                                padding: '1px 6px',
                                borderRadius: 'var(--radius-sm)',
                                fontWeight: 700,
                              }}
                            >
                              {s.rivalDominante.victorias} vic.
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>Ninguno</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {/* ── MODALES ──────────────────────────────────────────────── */}

      {/* 1. Modal Registro Rápido de Acta (30 seg) */}
      {modalActaAbierto && (
        <ModalRegistrarActa
          competidoresExistentes={directorio}
          alCerrar={() => setModalActaAbierto(false)}
        />
      )}

      {/* 2. Modal Head-to-Head (Cara a Cara Exhaustivo) */}
      {competidorH2HId && (
        <ModalHeadToHead
          competidorId={competidorH2HId}
          alCerrar={() => setCompetidorH2HId(null)}
          alSimular={(comp) => {
            setCompetidorH2HId(null);
            abrirSimuladorConCompetidor(comp);
          }}
        />
      )}

      {/* 3. Modal Fusionar Competidores Duplicados */}
      {modalFusionAbierto && (
        <ModalFusionarCompetidores
          competidores={directorio}
          alCerrar={() => setModalFusionAbierto(false)}
        />
      )}

      {/* 4. Modal Crear Competidor */}
      {modalCrearAbierto && (
        <ModalFormularioCompetidor
          competidor={null}
          alCerrar={() => setModalCrearAbierto(false)}
        />
      )}

      {/* 5. Modal Editar Competidor */}
      {competidorParaEditar && (
        <ModalFormularioCompetidor
          competidor={competidorParaEditar}
          alCerrar={() => setCompetidorParaEditar(null)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// COMPONENTE: SECCIÓN SIMULADOR DE PRECIO ÓPTIMO
// ─────────────────────────────────────────────────────────────

function SeccionSimulador({
  competidores,
  preseleccion,
}: {
  competidores: CompetidorDirectorio[];
  preseleccion: { competidorId?: string; perfil?: PerfilCompetidor } | null;
}) {
  const [presupuestoStr, setPresupuestoStr] = useState('30000000');
  const [competidorId, setCompetidorId] = useState(preseleccion?.competidorId ?? '');
  const [perfil, setPerfil] = useState<PerfilCompetidor>(preseleccion?.perfil ?? 'EQUILIBRADO');
  const [costoDirectoStr, setCostoDirectoStr] = useState('22000000');
  const [margenMinimoPct, setMargenMinimoPct] = useState(15);

  // Si cambia el competidor seleccionado, auto-asignar su perfil
  const alCambiarCompetidor = (id: string) => {
    setCompetidorId(id);
    if (id) {
      const comp = competidores.find((c) => c.id === id);
      if (comp) {
        setPerfil(comp.perfilEstrategico);
      }
    }
  };

  const presupuestoNum = Number(presupuestoStr.replace(/\D/g, '')) || 0;
  const costoDirectoNum = Number(costoDirectoStr.replace(/\D/g, '')) || 0;

  const parametrosSimulacion: SimularOfertaCompetidorInput | null = useMemo(() => {
    if (presupuestoNum <= 0) return null;
    return {
      presupuestoOficialNeto: presupuestoNum,
      competidorId: competidorId || undefined,
      perfil,
      costoDirectoNeto: costoDirectoNum > 0 ? costoDirectoNum : undefined,
      margenMinimoPct,
    };
  }, [presupuestoNum, competidorId, perfil, costoDirectoNum, margenMinimoPct]);

  const { data: resultado, isLoading, isError, error } = useSimularPrecio(parametrosSimulacion);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 20 }}>
      {/* Columna Izquierda: Formulario de Parámetros */}
      <Panel
        titulo="Parámetros de la Licitación"
        subtitulo="Ingresa los datos de las bases y costos de tu empresa para calcular la oferta óptima"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label htmlFor="sim-presupuesto">
              Presupuesto Oficial Neto de las Bases ($ CLP) <span style={{ color: 'var(--accent-red)' }}>*</span>
            </label>
            <input
              id="sim-presupuesto"
              type="text"
              required
              value={presupuestoNum > 0 ? presupuestoNum.toLocaleString('es-CL') : ''}
              onChange={(e) => setPresupuestoStr(e.target.value)}
              placeholder="Ej: 30.000.000"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700 }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="sim-competidor">Competidor Probable en este Proceso</label>
            <select
              id="sim-competidor"
              value={competidorId}
              onChange={(e) => alCambiarCompetidor(e.target.value)}
            >
              <option value="">Sin competidor específico (usar perfil genérico)</option>
              {competidores.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre} — {c.perfilEtiqueta}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sim-perfil">Perfil Estratégico del Rival</label>
            <select
              id="sim-perfil"
              value={perfil}
              onChange={(e) => setPerfil(e.target.value as PerfilCompetidor)}
            >
              {PERFILES_COMPETIDOR.map((p) => (
                <option key={p} value={p}>
                  {ETIQUETAS_PERFIL_COMPETIDOR[p]}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sim-costo">Costo Directo Neto Estimado del Proyecto ($ CLP)</label>
            <input
              id="sim-costo"
              type="text"
              value={costoDirectoNum > 0 ? costoDirectoNum.toLocaleString('es-CL') : ''}
              onChange={(e) => setCostoDirectoStr(e.target.value)}
              placeholder="Ej: 22.000.000"
              style={{ fontFamily: 'var(--font-mono)' }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Permite validar que la oferta ganadora no vulnere tu piso mínimo de margen.
            </span>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label htmlFor="sim-margen">Margen Mínimo Aceptable</label>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent-purple)' }}>
                {margenMinimoPct}%
              </span>
            </div>
            <input
              id="sim-margen"
              type="range"
              min={5}
              max={40}
              step={1}
              value={margenMinimoPct}
              onChange={(e) => setMargenMinimoPct(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--accent-purple)' }}
            />
          </div>
        </div>
      </Panel>

      {/* Columna Derecha: Proyección y Oferta Recomendada */}
      <Panel
        titulo="Oferta Recomendada & Estrategia Ganadora"
        subtitulo="Proyección matemática calculada por el motor analítico de Lexicore"
      >
        {presupuestoNum <= 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            Ingresa un presupuesto oficial para calcular la oferta recomendada.
          </div>
        ) : isLoading ? (
          <Cargando texto="Simulando mejor oferta frente al rival…" />
        ) : isError ? (
          <div style={{ color: 'var(--accent-red)', padding: 20 }}>
            {error instanceof Error ? error.message : 'Error en la simulación.'}
          </div>
        ) : resultado ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Tarjeta de Oferta Recomendada Principal */}
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.12), rgba(139, 92, 246, 0.1))',
                border: '2px solid var(--accent-red)',
                borderRadius: 'var(--radius-md)',
                padding: '20px 24px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 700,
                  color: 'var(--accent-red)',
                  marginBottom: 6,
                }}
              >
                Oferta Recomendada por Lexicore
              </div>

              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 28,
                  fontWeight: 900,
                  color: 'var(--text-primary)',
                }}
              >
                {formatearCLP(resultado.ofertaRecomendadaNeto)} <span style={{ fontSize: 14 }}>Neto</span>
              </div>

              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                Bruto con IVA 19%: <strong>{formatearCLP(Math.round(resultado.ofertaRecomendadaNeto * 1.19))}</strong>
              </div>

              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 12,
                  padding: '4px 12px',
                  borderRadius: 'var(--radius-full)',
                  background:
                    resultado.probabilidadGanarPct >= 60
                      ? 'var(--accent-green-soft)'
                      : 'var(--accent-yellow-soft)',
                  color:
                    resultado.probabilidadGanarPct >= 60
                      ? 'var(--accent-green)'
                      : 'var(--accent-yellow)',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {resultado.probabilidadGanarPct >= 60 ? '🟢' : '🟡'} {resultado.probabilidadGanarPct}% Probabilidad de Ganar
              </div>
            </div>

            {/* Métricas Detalladas de la Simulación */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                padding: 14,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Oferta Estimada del Rival</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700, marginTop: 2 }}>
                  {formatearCLP(resultado.ofertaProbableRivalNeto)}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>
                    ({resultado.ratioAplicadoPct}%)
                  </span>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Margen Proyectado</div>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 14,
                    fontWeight: 700,
                    marginTop: 2,
                    color:
                      resultado.margenProyectadoPct !== null && resultado.margenProyectadoPct >= margenMinimoPct
                        ? 'var(--accent-green)'
                        : 'var(--accent-yellow)',
                  }}
                >
                  {resultado.margenProyectadoPct !== null ? `${formatearPct(resultado.margenProyectadoPct, 1)}` : 'N/A'}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Piso Mínimo de Costo</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, marginTop: 2 }}>
                  {resultado.pisoMinimoAceptableNeto ? formatearCLP(resultado.pisoMinimoAceptableNeto) : 'No fijado'}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Perfil Aplicado</div>
                <div style={{ fontSize: 12, fontWeight: 600, marginTop: 2, color: 'var(--accent-blue)' }}>
                  {resultado.perfilEtiqueta.split('(')[0]?.trim()}
                </div>
              </div>
            </div>

            {/* Alerta de Conflicto de Margen */}
            {resultado.alerta && (
              <div
                style={{
                  background: 'var(--accent-yellow-soft)',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '12px 14px',
                  fontSize: 12,
                  color: 'var(--accent-yellow)',
                  lineHeight: 1.5,
                  display: 'flex',
                  gap: 8,
                }}
              >
                <IconoAlerta size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>{resultado.alerta}</span>
              </div>
            )}

            {/* Estrategia Comercial Recomendada */}
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                borderLeft: '3px solid var(--accent-blue)',
                padding: '12px 16px',
                borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-blue)', marginBottom: 4 }}>
                💡 TÁCTICA SUGERIDA PARA ESTA OFERTA:
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                {resultado.estrategiaSugerida}
              </div>
            </div>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MODAL 1: REGISTRO RÁPIDO DE ACTA DE LICITACIÓN (30 SEGUNDOS)
// ─────────────────────────────────────────────────────────────

function ModalRegistrarActa({
  competidoresExistentes,
  alCerrar,
}: {
  competidoresExistentes: CompetidorDirectorio[];
  alCerrar: () => void;
}) {
  const avisos = useAvisos();
  const mutacion = useRegistrarActaCompetencia();
  const licitacionesQuery = useLicitaciones({ porPagina: 100 });

  const [licitacionId, setLicitacionId] = useState('');
  const [modoNuevoCompetidor, setModoNuevoCompetidor] = useState(false);
  const [competidorId, setCompetidorId] = useState('');
  const [nombreCompetidor, setNombreCompetidor] = useState('');
  const [montoNetoStr, setMontoNetoStr] = useState('');
  const [plazoDiasStr, setPlazoDiasStr] = useState('');
  const [esGanador, setEsGanador] = useState(true);
  const [motivoPerdida, setMotivoPerdida] = useState<MotivoPerdida>('PRECIO');
  const [observaciones, setObservaciones] = useState('');

  const montoNetoNum = Number(montoNetoStr.replace(/\D/g, '')) || 0;
  const plazoDiasNum = Number(plazoDiasStr.replace(/\D/g, '')) || undefined;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!licitacionId) {
      avisos.error('Selecciona la licitación correspondiente.');
      return;
    }
    if (!modoNuevoCompetidor && !competidorId) {
      avisos.error('Selecciona el competidor o ingresa su nombre.');
      return;
    }
    if (modoNuevoCompetidor && !nombreCompetidor.trim()) {
      avisos.error('Escribe el nombre de la empresa competidora.');
      return;
    }
    if (montoNetoNum <= 0) {
      avisos.error('El monto neto ofertado debe ser mayor a 0.');
      return;
    }

    try {
      const payload: RegistrarActaLicitacionInput = {
        licitacionId,
        competidorId: !modoNuevoCompetidor ? competidorId : undefined,
        nombreCompetidor: modoNuevoCompetidor ? nombreCompetidor.trim() : undefined,
        montoNeto: montoNetoNum,
        plazoDias: plazoDiasNum,
        esGanador,
        motivoPerdida,
        observaciones: observaciones.trim() || undefined,
      };

      await mutacion.mutateAsync(payload);
      avisos.exito('Acta registrada exitosamente. El motor predictor ha actualizado sus estadísticas.');
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al registrar acta.');
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
      style={{ zIndex: 1050 }}
    >
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <div>
            <h2>⚡ Registro Rápido de Acta de Licitación</h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Ingresa los datos de adjudicación en 30 segundos para alimentar el banco de precios.
            </p>
          </div>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={15} />
          </button>
        </div>

        <form onSubmit={(e) => void enviar(e)}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Selección de Licitación */}
            <div className="form-group">
              <label htmlFor="acta-licitacion">
                Licitación de Referencia <span style={{ color: 'var(--accent-red)' }}>*</span>
              </label>
              <select
                id="acta-licitacion"
                required
                value={licitacionId}
                onChange={(e) => setLicitacionId(e.target.value)}
              >
                <option value="">Seleccionar licitación…</option>
                {licitacionesQuery.data?.datos.map((lic) => (
                  <option key={lic.id} value={lic.id}>
                    {lic.codigoMercadoPublico} — {lic.nombre} ({lic.organismo.nombre})
                  </option>
                ))}
              </select>
            </div>

            {/* Selección de Competidor */}
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label htmlFor="acta-competidor">
                  Empresa Competidora / Adjudicataria <span style={{ color: 'var(--accent-red)' }}>*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setModoNuevoCompetidor(!modoNuevoCompetidor)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--accent-blue)',
                    fontSize: 11.5,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {modoNuevoCompetidor ? '← Seleccionar existente' : '+ Escribir nuevo nombre'}
                </button>
              </div>

              {modoNuevoCompetidor ? (
                <input
                  id="acta-competidor-nuevo"
                  required
                  placeholder="Ej: TechPro SpA (se creará automáticamente)"
                  value={nombreCompetidor}
                  onChange={(e) => setNombreCompetidor(e.target.value)}
                />
              ) : (
                <select
                  id="acta-competidor"
                  required
                  value={competidorId}
                  onChange={(e) => setCompetidorId(e.target.value)}
                >
                  <option value="">Seleccionar del directorio…</option>
                  {competidoresExistentes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Monto Neto y Plazo */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="acta-monto">
                  Monto Neto Ofertado ($ CLP) <span style={{ color: 'var(--accent-red)' }}>*</span>
                </label>
                <input
                  id="acta-monto"
                  required
                  type="text"
                  placeholder="Ej: 24.500.000"
                  value={montoNetoNum > 0 ? montoNetoNum.toLocaleString('es-CL') : ''}
                  onChange={(e) => setMontoNetoStr(e.target.value)}
                  style={{ fontFamily: 'var(--font-mono)' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="acta-plazo">Plazo Ofertado (Días)</label>
                <input
                  id="acta-plazo"
                  type="number"
                  min={1}
                  placeholder="Ej: 20"
                  value={plazoDiasStr}
                  onChange={(e) => setPlazoDiasStr(e.target.value)}
                />
              </div>
            </div>

            {/* Ganador y Causa de Pérdida */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="acta-ganador">¿Es el Adjudicatario Ganador?</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38 }}>
                  <input
                    id="acta-ganador"
                    type="checkbox"
                    checked={esGanador}
                    onChange={(e) => setEsGanador(e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: 'var(--accent-green)' }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {esGanador ? 'Sí, adjudicó la licitación' : 'No, fue otro oferente'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="acta-motivo">Causa Principal de Derrota</label>
                <select
                  id="acta-motivo"
                  value={motivoPerdida}
                  onChange={(e) => setMotivoPerdida(e.target.value as MotivoPerdida)}
                >
                  {MOTIVOS_PERDIDA.map((motivo) => (
                    <option key={motivo} value={motivo}>
                      {ETIQUETAS_MOTIVO_PERDIDA[motivo]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Observaciones */}
            <div className="form-group">
              <label htmlFor="acta-obs">Observaciones o Puntos Clave del Acta</label>
              <textarea
                id="acta-obs"
                rows={2}
                placeholder="Ej: Adjudicado con puntaje máximo en oferta económica. Descalificaron al 3er postor por falta de boleta."
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar} disabled={mutacion.isPending}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={mutacion.isPending || !licitacionId || montoNetoNum <= 0}
            >
              <IconoCheck size={14} />
              {mutacion.isPending ? 'Guardando Acta…' : 'Guardar Acta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MODAL 2: CARA A CARA (HEAD-TO-HEAD) EXHAUSTIVO
// ─────────────────────────────────────────────────────────────

function ModalHeadToHead({
  competidorId,
  alCerrar,
  alSimular,
}: {
  competidorId: string;
  alCerrar: () => void;
  alSimular: (competidor: CompetidorDirectorio) => void;
}) {
  const { data: h2h, isLoading, isError, error, refetch } = useHeadToHead(competidorId);

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
      style={{ zIndex: 1050 }}
    >
      <div className="modal" style={{ maxWidth: 840 }}>
        {isLoading ? (
          <div style={{ padding: 40 }}>
            <Cargando texto="Cargando historial cara a cara…" />
          </div>
        ) : isError ? (
          <div style={{ padding: 30 }}>
            <ErrorCarga error={error} reintentar={() => void refetch()} />
          </div>
        ) : h2h ? (
          <>
            <div className="modal-header">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 800 }}>⚔️ Cara a Cara vs {h2h.competidor.nombre}</h2>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-full)',
                      background: 'rgba(59, 130, 246, 0.15)',
                      color: 'var(--accent-blue)',
                    }}
                  >
                    {h2h.competidor.perfilEtiqueta}
                  </span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                  {h2h.competidor.rut ? `RUT: ${h2h.competidor.rut} · ` : ''}
                  Historial detallado licitación por licitación donde competimos directamente.
                </p>
              </div>
              <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
                <IconoCerrar size={15} />
              </button>
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* 4 KPIs Cara a Cara */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 10,
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 14,
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Enfrentamientos</div>
                  <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>
                    {h2h.resumenCaraACara.totalEnfrentamientos}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Balance Victorias</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>
                    <span style={{ color: 'var(--accent-green)' }}>{h2h.resumenCaraACara.ganadasPorNosotros}G</span>
                    {' vs '}
                    <span style={{ color: 'var(--accent-red)' }}>{h2h.resumenCaraACara.ganadasPorRival}P</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>
                      ({h2h.resumenCaraACara.winRateVsRivalPct}%)
                    </span>
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Brecha de Precio</div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      marginTop: 2,
                      color:
                        h2h.resumenCaraACara.brechaPrecioPromedioPct !== null &&
                        h2h.resumenCaraACara.brechaPrecioPromedioPct > 0
                          ? 'var(--accent-red)'
                          : 'var(--accent-green)',
                    }}
                  >
                    {h2h.resumenCaraACara.brechaPrecioPromedioPct !== null
                      ? `${h2h.resumenCaraACara.brechaPrecioPromedioPct > 0 ? '+' : ''}${formatearPct(h2h.resumenCaraACara.brechaPrecioPromedioPct, 1)}`
                      : '—'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Monto que se llevó</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, color: 'var(--text-primary)' }}>
                    {formatearCLP(h2h.resumenCaraACara.montoGanadoPorRival)}
                  </div>
                </div>
              </div>

              {/* Notas de inteligencia si existen */}
              {h2h.competidor.notas && (
                <div
                  style={{
                    fontSize: 12,
                    fontStyle: 'italic',
                    color: 'var(--text-secondary)',
                    background: 'rgba(139, 92, 246, 0.08)',
                    borderLeft: '3px solid var(--accent-purple)',
                    padding: '8px 12px',
                    borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
                  }}
                >
                  📝 <strong>Notas de Inteligencia:</strong> "{h2h.competidor.notas}"
                </div>
              )}

              {/* Tabla de Licitaciones Disputadas */}
              <div>
                <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                  Licitaciones Disputadas ({h2h.historial.length})
                </h4>

                {h2h.historial.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                    Aún no hay licitaciones registradas contra este competidor.
                  </div>
                ) : (
                  <div className="tabla-scroll" style={{ maxHeight: 320 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Licitación / Organismo</th>
                          <th style={{ textAlign: 'right' }}>Presupuesto</th>
                          <th style={{ textAlign: 'right' }}>Nuestra Oferta</th>
                          <th style={{ textAlign: 'right' }}>Oferta Rival</th>
                          <th style={{ textAlign: 'center' }}>Resultado</th>
                          <th style={{ textAlign: 'right' }}>Diferencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {h2h.historial.map((item) => (
                          <tr key={item.licitacionId}>
                            <td>
                              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                {item.codigoMercadoPublico}
                              </div>
                              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                                {item.nombreLicitacion}
                              </div>
                              <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
                                🏛️ {item.organismo.nombre}
                              </div>
                            </td>

                            <td className="td-mono" style={{ textAlign: 'right' }}>
                              {item.presupuestoOficialNeto > 0
                                ? formatearCLP(item.presupuestoOficialNeto)
                                : '—'}
                            </td>

                            <td className="td-mono" style={{ textAlign: 'right' }}>
                              <div>{formatearCLP(item.nuestraOferta.montoNeto)}</div>
                              {item.nuestraOferta.plazoDias > 0 && (
                                <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                                  {formatearDias(item.nuestraOferta.plazoDias)}
                                </div>
                              )}
                            </td>

                            <td className="td-mono" style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: item.ofertaRival.esGanador ? 700 : 400 }}>
                                {formatearCLP(item.ofertaRival.montoNeto)}
                              </div>
                              {item.ofertaRival.plazoDias && item.ofertaRival.plazoDias > 0 && (
                                <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                                  {formatearDias(item.ofertaRival.plazoDias)}
                                </div>
                              )}
                            </td>

                            <td style={{ textAlign: 'center' }}>
                              {item.resultado === 'GANADA_POR_NOSOTROS' ? (
                                <span
                                  style={{
                                    background: 'var(--accent-green-soft)',
                                    color: 'var(--accent-green)',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-full)',
                                  }}
                                >
                                  Ganamos nosotros 🏆
                                </span>
                              ) : item.resultado === 'GANADA_POR_RIVAL' ? (
                                <span
                                  style={{
                                    background: 'var(--accent-red-soft)',
                                    color: 'var(--accent-red)',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-full)',
                                  }}
                                >
                                  Ganó el rival
                                </span>
                              ) : (
                                <span
                                  style={{
                                    background: 'var(--bg-elevated)',
                                    color: 'var(--text-muted)',
                                    fontSize: 11,
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-full)',
                                  }}
                                >
                                  Ganó un tercero
                                </span>
                              )}
                            </td>

                            <td
                              className="td-mono"
                              style={{
                                textAlign: 'right',
                                color:
                                  item.brechaPrecioPct !== null && item.brechaPrecioPct > 0
                                    ? 'var(--accent-red)'
                                    : 'var(--accent-green)',
                              }}
                            >
                              {item.brechaPrecioPct !== null
                                ? `${item.brechaPrecioPct > 0 ? '+' : ''}${formatearPct(item.brechaPrecioPct, 1)}`
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  alSimular({
                    id: h2h.competidor.id,
                    nombre: h2h.competidor.nombre,
                    rut: h2h.competidor.rut,
                    sitioWeb: h2h.competidor.sitioWeb,
                    notas: h2h.competidor.notas,
                    totalOfertas: h2h.resumenCaraACara.totalEnfrentamientos,
                    victorias: h2h.resumenCaraACara.ganadasPorRival,
                    montoGanado: h2h.resumenCaraACara.montoGanadoPorRival,
                    caraACara: {
                      enfrentamientosTotales: h2h.resumenCaraACara.totalEnfrentamientos,
                      ganadasPorNosotros: h2h.resumenCaraACara.ganadasPorNosotros,
                      ganadasPorRival: h2h.resumenCaraACara.ganadasPorRival,
                      winRateVsRivalPct: h2h.resumenCaraACara.winRateVsRivalPct,
                    },
                    brechaPrecioPromedioPct: h2h.resumenCaraACara.brechaPrecioPromedioPct,
                    ratioPresupuestoPromedioPct: null,
                    perfilEstrategico: h2h.competidor.perfilEstrategico,
                    perfilEtiqueta: h2h.competidor.perfilEtiqueta,
                    terrenoFuerte: [],
                  });
                }}
              >
                🎯 Simular Precio contra este rival
              </button>

              <button type="button" className="btn btn-secondary" onClick={alCerrar}>
                Cerrar
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MODAL 3: FUSIÓN DE COMPETIDORES DUPLICADOS (1 CLIC)
// ─────────────────────────────────────────────────────────────

function ModalFusionarCompetidores({
  competidores,
  alCerrar,
}: {
  competidores: CompetidorDirectorio[];
  alCerrar: () => void;
}) {
  const avisos = useAvisos();
  const mutacion = useFusionarCompetidores();

  const [origenId, setOrigenId] = useState('');
  const [destinoId, setDestinoId] = useState('');

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!origenId || !destinoId) {
      avisos.error('Selecciona el competidor de origen y el de destino.');
      return;
    }
    if (origenId === destinoId) {
      avisos.error('El competidor origen y destino no pueden ser el mismo.');
      return;
    }

    try {
      const payload: FusionarCompetidoresInput = {
        competidorOrigenId: origenId,
        competidorDestinoId: destinoId,
      };

      const resp = await mutacion.mutateAsync(payload);
      avisos.exito(resp.mensaje ?? 'Competidores fusionados exitosamente.');
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al fusionar competidores.');
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
      style={{ zIndex: 1050 }}
    >
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <div>
            <h2>🔀 Fusionar Competidores Duplicados</h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Combina dos registros en uno solo unificando ofertas y notas sin pérdida de datos.
            </p>
          </div>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={15} />
          </button>
        </div>

        <form onSubmit={(e) => void enviar(e)}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                borderRadius: 'var(--radius-sm)',
                padding: '10px 14px',
                fontSize: 12,
                color: 'var(--accent-red)',
                lineHeight: 1.5,
              }}
            >
              ⚠️ <strong>Atención:</strong> El competidor origen será eliminado permanentemente. Todas sus ofertas,
              victorias y notas de inteligencia se transferirán al competidor destino.
            </div>

            <div className="form-group">
              <label htmlFor="comp-origen">
                Competidor a Eliminar (Duplicado) <span style={{ color: 'var(--accent-red)' }}>*</span>
              </label>
              <select
                id="comp-origen"
                required
                value={origenId}
                onChange={(e) => setOrigenId(e.target.value)}
              >
                <option value="">Seleccionar competidor duplicado…</option>
                {competidores.map((c) => (
                  <option key={c.id} value={c.id} disabled={c.id === destinoId}>
                    {c.nombre} ({c.totalOfertas} ofertas)
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="comp-destino">
                Competidor que Permanecerá (Oficial) <span style={{ color: 'var(--accent-red)' }}>*</span>
              </label>
              <select
                id="comp-destino"
                required
                value={destinoId}
                onChange={(e) => setDestinoId(e.target.value)}
              >
                <option value="">Seleccionar competidor destino…</option>
                {competidores.map((c) => (
                  <option key={c.id} value={c.id} disabled={c.id === origenId}>
                    {c.nombre} ({c.totalOfertas} ofertas)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar} disabled={mutacion.isPending}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={mutacion.isPending || !origenId || !destinoId || origenId === destinoId}
            >
              {mutacion.isPending ? 'Fusionando…' : 'Confirmar Fusión'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MODAL 4: FORMULARIO CREAR / EDITAR COMPETIDOR
// ─────────────────────────────────────────────────────────────

function ModalFormularioCompetidor({
  competidor,
  alCerrar,
}: {
  competidor: CompetidorDirectorio | null;
  alCerrar: () => void;
}) {
  const avisos = useAvisos();
  const mutCrear = useCrearCompetidor();
  const mutActualizar = useActualizarCompetidor();

  const [nombre, setNombre] = useState(competidor?.nombre ?? '');
  const [rut, setRut] = useState(competidor?.rut ?? '');
  const [sitioWeb, setSitioWeb] = useState(competidor?.sitioWeb ?? '');
  const [notas, setNotas] = useState(competidor?.notas ?? '');

  const guardando = mutCrear.isPending || mutActualizar.isPending;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) {
      avisos.error('El nombre de la empresa es obligatorio.');
      return;
    }

    try {
      if (competidor) {
        const payload: ActualizarCompetidorInput = {
          nombre: nombre.trim(),
          rut: rut.trim() || undefined,
          sitioWeb: sitioWeb.trim() || undefined,
          notas: notas.trim() || undefined,
        };
        await mutActualizar.mutateAsync({ id: competidor.id, datos: payload });
        avisos.exito('Competidor actualizado exitosamente.');
      } else {
        const payload: CrearCompetidorInput = {
          nombre: nombre.trim(),
          rut: rut.trim() || undefined,
          sitioWeb: sitioWeb.trim() || undefined,
          notas: notas.trim() || undefined,
        };
        await mutCrear.mutateAsync(payload);
        avisos.exito('Competidor creado exitosamente.');
      }
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al guardar competidor.');
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
      style={{ zIndex: 1050 }}
    >
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>{competidor ? 'Editar Rival' : 'Nuevo Competidor'}</h2>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={15} />
          </button>
        </div>

        <form onSubmit={(e) => void enviar(e)}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label htmlFor="comp-nombre">
                Razón Social / Nombre Comercial <span style={{ color: 'var(--accent-red)' }}>*</span>
              </label>
              <input
                id="comp-nombre"
                required
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: TechPro SpA"
              />
            </div>

            <div className="form-group">
              <label htmlFor="comp-rut">RUT Institucional (Opcional)</label>
              <input
                id="comp-rut"
                value={rut}
                onChange={(e) => setRut(e.target.value)}
                placeholder="Ej: 76.452.120-K"
              />
            </div>

            <div className="form-group">
              <label htmlFor="comp-web">Sitio Web (Opcional)</label>
              <input
                id="comp-web"
                value={sitioWeb}
                onChange={(e) => setSitioWeb(e.target.value)}
                placeholder="https://techpro.cl"
              />
            </div>

            <div className="form-group">
              <label htmlFor="comp-notas">Notas de Inteligencia de Mercado</label>
              <textarea
                id="comp-notas"
                rows={3}
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Ej: Suelen subcontratar desarrollo. Presentan ofertas muy agresivas en municipalidades de la RM."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar} disabled={guardando}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={guardando || !nombre.trim()}>
              <IconoCheck size={14} />
              {guardando ? 'Guardando…' : competidor ? 'Guardar Cambios' : 'Crear Competidor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

